import type { InversionDef } from "../unexpected.banks";
import { INVERSIONS } from "../unexpected.banks";

export interface UnexpectedLadder {
  id: string;
  name: string;
  invertedRule: string;
  earthPremise: string;
  thisWorldPremise: string;
  standingLabel: string;
  socialClassLabel: string;
  wealthBracket: string;
  wealthExplanation: string;
  workplaceType: string;
  societySummary: string;
  lawsSummary: string;
  cultureSummary: string;
  institutionsSummary: string;
  expectationsSummary: string;
  opportunitySummary: string;
  divergenceD1: { title: string; text: string; effects: string };
  divergenceD2: { title: string; text: string; effects: string };
  divergenceD3: { title: string; text: string; effects: string };
  divergenceD4: { title: string; text: string; effects: string };
}

/**
 * 12 distinct "Inverted Ladders" for The Unexpected Self.
 * These invert Earth assumptions regarding status, success, work, belonging, and identity.
 */
export const UNEXPECTED_LADDERS: readonly UnexpectedLadder[] = [
  {
    id: "maintainers_over_founders",
    name: "The Maintenance Hegemony",
    invertedRule: "Status belongs to those who keep fragile things running quietly across generations, while novelty-seeking founders and disruptors are viewed as reckless dependents.",
    earthPremise: "Earth rewards glamorous founders, disruptive innovators, and high-velocity asset creators while underpaying custodians and repairers.",
    thisWorldPremise: "Here, preserving existing harmony and mending community fabric commands supreme civic honors and generational equity.",
    standingLabel: "Master Custodian of the Unbroken Thread",
    socialClassLabel: "High Guild Conservator",
    wealthBracket: "Endowed Maintenance Trustee",
    wealthExplanation: "Sustained by perpetual civic dividends tied directly to the zero-downtime record of neighborhood infrastructure and social calm.",
    workplaceType: "A sprawling municipal restorative workshop filled with diagnostic brass apparatuses, re-tempering hearths, and historical wear registers.",
    societySummary: "A civilization structured around preventative care, structural endurance, and deep intergenerational gratitude.",
    lawsSummary: "The Decennial Continuity Acts mandate that no new enterprise may break ground without three bonded conservators guaranteeing its five-decade maintenance.",
    cultureSummary: "Public parades celebrate elderly repair artisans while statues of self-proclaimed visionaries are deliberately avoided.",
    institutionsSummary: "The Conservators' High Bench, the Diagnostic Guildhalls, and the Ledger of Unsung Repairs.",
    expectationsSummary: "Every citizen must complete a mandatory multi-year apprenticeship in basic sanitation, structural patching, or emotional reconciliation.",
    opportunitySummary: "Unbounded prestige for those who can diagnose stress points before fractures appear in stone, wood, or civic trust.",
    divergenceD1: {
      id: "d1",
      title: "The Inverted Value of Repair",
      text: "Civilization rejected planned obsolescence and disruptive venture blitzscaling three centuries ago, placing maintenance at the apex of law and honor.",
      effects: "Societal resources flow toward keeping existing systems pristine rather than manufacturing endless disposable novelties.",
    },
    divergenceD2: {
      id: "d2",
      title: "Generational Continuity Charters",
      text: "Property and enterprises are held in trust for future centuries, requiring strict proof of sustainable upkeep before transfer.",
      effects: "Speculative financial stripping is illegal; wealth is indexed to physical and social durability.",
    },
    divergenceD3: {
      id: "d3",
      title: "The Ethic of Quiet Stewardship",
      text: "Fame for aggressive disruption is treated as public nuisance; the highest decorations belong to those whose work produces uninterrupted tranquility.",
      effects: "Leadership selection elevates patient listeners, diagnostic technicians, and restorative mediators.",
    },
    divergenceD4: {
      id: "d4",
      title: "Open Diagnostic Commons",
      text: "All blueprinted technologies and civic algorithms are completely transparent and modifiable by any certified guild repairer.",
      effects: "Monopolistic trade secrets are non-existent, ensuring immediate local resilience against any breakdown.",
    },
  },
  {
    id: "rest_as_highest_craft",
    name: "The Commonwealth of Deliberate Slowness",
    invertedRule: "Rest, contemplation, and unhurried stillness are the supreme civic virtues; frenetic hustle and relentless busyness are treated as tragic emotional impairments.",
    earthPremise: "Earth measures worth by billable hours, frantic productivity, and competitive exhaustion.",
    thisWorldPremise: "Here, those who master deep stillness, restful presence, and contemplative leisure anchor the sanity of entire districts.",
    standingLabel: "Master of Serene Intervals",
    socialClassLabel: "Conclave Stillness Elder",
    wealthBracket: "Abundantly Unhurried",
    wealthExplanation: "Compensated through communal tranquility shares that increase in value the fewer hurried emergencies your district experiences.",
    workplaceType: "An airy tea pavilion and courtyard sanctuary wrapped in fragrant cedar shutters, windchimes, and cushioned alcoves for unhurried thought.",
    societySummary: "A peaceful commonwealth where the municipal pace is calibrated to human biorhythms and contemplative restoration.",
    lawsSummary: "The Diurnal Rest Code strictly forbids after-sunset commercial bargaining and penalizes employers whose staff look visibly hurried.",
    cultureSummary: "Celebrates the art of quiet observation, afternoon naps, exquisite poetry, and the unhurried ripening of complicated decisions.",
    institutionsSummary: "The Sanctuaries of Pause, the Assembly of Harmonic Tempos, and the Office of Calibrated Breathing.",
    expectationsSummary: "Citizens are expected to spend at least two full daylight hours daily in unmonetized contemplation or gentle neighborly conversation.",
    opportunitySummary: "High civic honor for counselors, thinkers, and artisans who can de-escalate collective anxieties and restore community ease.",
    divergenceD1: {
      id: "d1",
      title: "The Sanctity of Human Pace",
      text: "Industrial acceleration was halted when citizens recognized that hyper-productivity degrades empathy and collective wisdom.",
      effects: "Clocks measure seasons and harmonic shifts rather than punitive seconds; deadlines yield to organic completion.",
    },
    divergenceD2: {
      id: "d2",
      title: "Universal Solace Dividend",
      text: "Every resident receives unconditional access to food, shelter, and quiet gardens, eliminating fear-driven wage panic.",
      effects: "Labor is undertaken from genuine vocational calling or mutual affection rather than existential survival dread.",
    },
    divergenceD3: {
      id: "d3",
      title: "De-escalation of Urgency",
      text: "Manufactured panics and manufactured shortages are classified as civic offenses against public health.",
      effects: "Institutions deliberate for full moon cycles before ratifying regional treaties or zoning modifications.",
    },
    divergenceD4: {
      id: "d4",
      title: "Architecture of Stillness",
      text: "Public plazas are acoustically dampened with living moss and water features designed to absorb echo and soothe nerves.",
      effects: "Cities function as restorative sanctuaries rather than high-decibel commercial corridors.",
    },
  },
  {
    id: "best_listener_status",
    name: "The Auricular Republic",
    invertedRule: "The highest authority and prestige belong not to persuasive orators or vocal leaders, but to the deepest listeners who absorb community nuance without interrupting.",
    earthPremise: "Earth platforms the loudest voices, aggressive debaters, and charismatic self-promoters while ignoring quiet witnesses.",
    thisWorldPremise: "Here, speaking is heavily rationed and listeners who synthesize opposing grief into silent clarity govern assemblies.",
    standingLabel: "High Witness of Unspoken Truths",
    socialClassLabel: "Primate of the Attentive Ear",
    wealthBracket: "Civically Recompensed Confidant",
    wealthExplanation: "Guaranteed lifelong sustenance from the Civic Resonance Fund in exchange for bearing impartial witness to neighborly disputes.",
    workplaceType: "An acoustic consultation chamber lined with heavy felt, resonant cedar tuning blocks, and low hearth tables where disputants sit in quiet.",
    societySummary: "A reflective society that resolves structural conflicts through patient, non-judgmental collective listening.",
    lawsSummary: "The Law of Attentive Silence allows any accused citizen to demand a full three-day period of silent witness before any verdict can be deliberated.",
    cultureSummary: "Speech is deliberate, sparse, and carefully weighed; eloquence is judged by how few words are required to articulate a shared truth.",
    institutionsSummary: "The Chambers of Quiet Witness, the High Ear Registry, and the Academy of Nuanced Synthesis.",
    expectationsSummary: "Leaders must pass rigorous exams demonstrating they can summarize opponents' grievances better than their opponents can.",
    opportunitySummary: "Immense respect for practitioners who can sit inside ferocious discord and draw out the unarticulated common ground.",
    divergenceD1: {
      id: "d1",
      title: "The Inversion of Eloquence",
      text: "Rhetorical trickery and loud broadcast monopolization were outlawed after historical demagogues nearly destroyed civil life.",
      effects: "Political speech is limited to brief factual propositions followed by extended periods of communal evaluation.",
    },
    divergenceD2: {
      id: "d2",
      title: "Institutionalized Attunement",
      text: "Every public board must appoint an impartial Witness whose sole power is to veto actions where any party was ignored.",
      effects: "Marginalized and vulnerable perspectives cannot be overridden by numerical majorities without thorough hearing.",
    },
    divergenceD3: {
      id: "d3",
      title: "Acoustic Humility",
      text: "Social stature is signaled by volume control; shouting is seen as infant helplessness while quiet speaking commands absolute focus.",
      effects: "Daily life is peaceful, attentive, and free from competitive audio dominance.",
    },
    divergenceD4: {
      id: "d4",
      title: "Confidentiality as Sacred Trust",
      text: "What is spoken to a designated listener is inviolable by state subpoena or family claim.",
      effects: "Citizens possess an unshakeable emotional safety net where vulnerability is met with absolute sanctuary.",
    },
  },
  {
    id: "generous_disinvestment",
    name: "The Potlatch Commonwealth",
    invertedRule: "Wealth is measured strictly by how much capital, equipment, and knowledge you have permanently given away to the public commons.",
    earthPremise: "Earth glorifies hoarding, private accumulation, and billionaire dynasties while treating disinvestment as financial failure.",
    thisWorldPremise: "Here, a citizen who dies with private riches is buried in embarrassment, while those who divest into the commons sit at the head of every feast.",
    standingLabel: "Grand Divestor of the Outer Ward",
    socialClassLabel: "Patron of the Depleted Estate",
    wealthBracket: "Honorably Unburdened",
    wealthExplanation: "Owns practically nothing privately, but holds universal guest rights, dining seats, and workshop passes in every household across the district.",
    workplaceType: "A bustling communal distribution dispensary where tools, books, seeds, and artistic creations are gifted freely to visiting travelers.",
    societySummary: "A joyful gift economy where status correlates with radical generosity and hoarding is considered an acute mental malady.",
    lawsSummary: "The Satiation Statutes require any business generating surplus beyond operational reserves to throw a seasonal public giveaway.",
    cultureSummary: "Festivals of Surplus feature elaborate celebrations where master artisans surrender their finest tools to ambitious young apprentices.",
    institutionsSummary: "The Hall of Open Ledgers, the Commons Trust Syndicate, and the Fellowship of Joyful Divestment.",
    expectationsSummary: "The more skillful you become, the more you are expected to train your successors and distribute your workshop machinery.",
    opportunitySummary: "Total societal love and absolute lifelong security for those who enrich the public domain through their talents.",
    divergenceD1: {
      id: "d1",
      title: "The Bankruptcy of Hoarding",
      text: "Centuries ago, compound interest and private accumulation were replaced by a wealth tax that scales exponentially with idle capital.",
      effects: "Citizens actively search for creative, inspiring ways to transfer their resources into public parks, guilds, and schools.",
    },
    divergenceD2: {
      id: "d2",
      title: "Universal Reciprocal Hospitality",
      text: "Anyone who has contributed to the Commons Trust is legally entitled to lodging, dining, and workspace in any township.",
      effects: "Physical private ownership becomes an unnecessary burden when the entire commonwealth functions as a shared estate.",
    },
    divergenceD3: {
      id: "d3",
      title: "Praise for the Empty Hands",
      text: "Funeral elegies celebrate the inventory of things the departed gave away during life rather than what they left in private safes.",
      effects: "Heirs inherit reputations for civic generosity rather than locked vaults of inherited entitlement.",
    },
    divergenceD4: {
      id: "d4",
      title: "Community Capital Stewards",
      text: "Large enterprises are structured as temporary stewardship charters that automatically transition to cooperative worker trusts.",
      effects: "Economic monopoly is structurally impossible, preventing oligarchic capture of government.",
    },
  },
  {
    id: "septennial_rotation",
    name: "The Metamorphic Guilds",
    invertedRule: "Lifelong careers are strictly discouraged; every citizen is expected to abandon their trade every seven years and restart as a humble novice in an unrelated field.",
    earthPremise: "Earth demands hyper-specialization, narrow career ladders, and penalizes mid-career pivots as wasted credentials.",
    thisWorldPremise: "Here, staying in one profession for decades is viewed as cognitive calcification; the most admired citizens have reinvented themselves five times.",
    standingLabel: "Quad-Discipline Journeyman",
    socialClassLabel: "Metamorphic Fellow",
    wealthBracket: "Broadly Adaptable",
    wealthExplanation: "Maintains equity stakes across multiple vocational lodges earned through successive seven-year terms of honest novicehood.",
    workplaceType: "A hybrid atelier combining mechanical drafting tables, biological terrariums, and ceramic kilns, reflecting multiple mastered trades.",
    societySummary: "A fluid, intellectually agile commonwealth where generational wisdom is cross-pollinated across every craft and discipline.",
    lawsSummary: "The Septennial Transition Ordinance grants every resident a full year of paid sabbatical and relocation stipends at the close of each cycle.",
    cultureSummary: "Celebrates the beginner's mind, joyful clumsiness, and the cross-fertilization of unrelated methodologies.",
    institutionsSummary: "The Transition Lodges, the Novice Exchange Bureau, and the Registry of Varied Callings.",
    expectationsSummary: "When your seventh year ends, you must formally hand over your position to a newcomer and report to a new guild hall with an empty satchel.",
    opportunitySummary: "Unmatched creative breakthroughs born from applying agricultural logic to metallurgy or musical acoustics to city waterworks.",
    divergenceD1: {
      id: "d1",
      title: "The Outlawing of Professional Calcification",
      text: "Specialist cliques that monopolized power were broken by constitutional mandates requiring regular vocational mobility.",
      effects: "Bureaucracies cannot entrench themselves because personnel rotate completely every seven years.",
    },
    divergenceD2: {
      id: "d2",
      title: "The Universal Novice Sabbatical",
      text: "Year seven of each cycle is funded as an open exploration period where citizens travel, study, and apprentice under masters.",
      effects: "Mid-life crises and occupational burnout are replaced by structured, celebrated renewal.",
    },
    divergenceD3: {
      id: "d3",
      title: "Cross-Disciplinary Synthetics",
      text: "Technological innovation accelerates because people carry perspectives from masonry into surgery or poetry into navigation.",
      effects: "Dogmatic academic silos do not exist; all knowledge is treated as interconnected practical wisdom.",
    },
    divergenceD4: {
      id: "d4",
      title: "Egalitarian Empathy",
      text: "Because everyone has recently been a bumbling beginner, master artisans treat apprentices with gentle, remembering patience.",
      effects: "Workplace bullying and credentialist snobbery are eradicated by lived memory of vulnerability.",
    },
  },
  {
    id: "role_over_name",
    name: "The Commonwealth of the Masked Calling",
    invertedRule: "Individual names are kept strictly private within family parlors; in public and professional life, citizens are addressed solely by their temporary functional role.",
    earthPremise: "Earth obsesses over personal branding, influencer egoism, and immortalizing individual surnames.",
    thisWorldPremise: "Here, personal ego is protected by public anonymity, allowing work to be judged purely on its merits and shielding citizens from reputational toxicity.",
    standingLabel: "The Seventh Weaver of Canal Bridges",
    socialClassLabel: "Civic Vocationer",
    wealthBracket: "Functionally Guaranteed",
    wealthExplanation: "Tied directly to the current mantle of office; resources accompany the workstation rather than accumulating in private accounts.",
    workplaceType: "A dignified municipal office where all desks, seals, and drafting instruments bear only the insignia of the post, untouched by personal vanity.",
    societySummary: "A remarkably objective, calm society where nepotism and celebrity cults cannot take root.",
    lawsSummary: "The Civic Modesty Statutes prohibit the publication of private surnames in connection with public contracts or commercial enterprises.",
    cultureSummary: "Focuses on collective masterpieces; architecture and scientific treatises are signed by the office and year rather than an individual author.",
    institutionsSummary: "The College of Civic Functions, the Chamber of the Unnamed Seal, and the Registry of Mantles.",
    expectationsSummary: "You must leave personal grievances and vanity at the door when stepping into your working uniform and functional role.",
    opportunitySummary: "Pure meritocracy where youth, background, and social origins are masked behind the neutral dignity of the calling.",
    divergenceD1: {
      id: "d1",
      title: "The Abolition of the Ego Economy",
      text: "Personal branding was identified as the root cause of historical political vanity and replaced with anonymous functional stewardship.",
      effects: "Citizens live free from the exhaustion of curating an idealized public persona.",
    },
    divergenceD2: {
      id: "d2",
      title: "The Sacred Private Identity",
      text: "Your real name and idiosyncratic personal self belong exclusively to loved ones, sheltered from commercial exploitation.",
      effects: "Private life is authentic and untainted by corporate networking or professional posturing.",
    },
    divergenceD3: {
      id: "d3",
      title: "Impartial Public Assessment",
      text: "Works of art, scientific theorems, and civic policies are evaluated without knowing the author's age, gender, or pedigree.",
      effects: "Nepotism is structurally disabled; good ideas prevail regardless of who formulated them.",
    },
    divergenceD4: {
      id: "d4",
      title: "Fluid Succession",
      text: "When a practitioner retires, their mantle passes seamlessly to a trained successor without traumatic institutional disruption.",
      effects: "Enterprises endure for centuries without depending on the fragile personality of a single charismatic founder.",
    },
  },
  {
    id: "quiet_finishing",
    name: "The Guild of the Final Stone",
    invertedRule: "Prestige belongs to the finishers—those who bring uncompleted, messy projects to tidy closure—while starting things is viewed as cheap and easy.",
    earthPremise: "Earth celebrates ribbon-cuttings, charismatic kickoffs, and visionary founders, often leaving half-finished messes behind.",
    thisWorldPremise: "Here, anyone can begin an idea, but the person who stays for five years to sweep the floors, polish the joints, and write the final report is crowned hero.",
    standingLabel: "Master Closer of Abandoned Labors",
    socialClassLabel: "High Terminal Registrar",
    wealthBracket: "Post-Completion Annuity",
    wealthExplanation: "Funded by completion bounties paid out only when all trailing disputes, loose ends, and environmental restoration covenants are fully resolved.",
    workplaceType: "A quiet archival sanctum surrounded by completed model prototypes, finalized ledger boxes, and certificates of graceful closure.",
    societySummary: "A tidy, highly functional civilization with virtually zero abandoned projects, half-built ruins, or unresolved legal debts.",
    lawsSummary: "The Closure Mandate requires that fifty percent of any capital budget be held in escrow until the project's decommissioning or final handover.",
    cultureSummary: "Celebrates the quiet satisfaction of the last stroke of the brush, the final signature, and the clean workbench at twilight.",
    institutionsSummary: "The Chamber of Final Accounts, the Finishing Guild of the Province, and the Registry of Completed Labors.",
    expectationsSummary: "Never abandon an endeavor midway; if you must leave, you must hire and train a bonded closer to finish your promises.",
    opportunitySummary: "Enormous compensation for reliable, patient minds who can disentangle snarled initiatives and guide them across the finish line.",
    divergenceD1: {
      id: "d1",
      title: "The Primacy of Completion",
      text: "Starting initiatives is easy; civilization recognized that real societal wealth lies in finishing what was begun.",
      effects: "Cities are orderly, functional, and devoid of speculative half-built eyesores.",
    },
    divergenceD2: {
      id: "d2",
      title: "The Closing Escrow System",
      text: "Financial rewards are weighted toward the final twenty percent of an initiative, ensuring leaders cannot abandon difficult execution phases.",
      effects: "Disruptive founders are forced to see their consequences through to the very end.",
    },
    divergenceD3: {
      id: "d3",
      title: "Celebration of the Sweep",
      text: "Public festivals commemorate the closure of old municipal eras and the clean retirement of obsolete laws.",
      effects: "Legal codes remain concise, intelligible, and unburdened by centuries of dead statutes.",
    },
    divergenceD4: {
      id: "d4",
      title: "The Stewardship of Transitions",
      text: "Specialists in graceful decommissioning ensure that retiring technologies are dismantled without leaving environmental scars.",
      effects: "Industrial transitions happen smoothly without leaving toxic rustbelts behind.",
    },
  },
  {
    id: "failure_as_curriculum",
    name: "The Fellowship of the Informative Blunder",
    invertedRule: "Mistakes, failures, and structural breakdowns are treated as precious empirical curriculum; people who have failed spectacularly and analyzed it transparently hold the highest advisory posts.",
    earthPremise: "Earth penalizes failure, buries mistakes under public relations spin, and worships frictionless resumes.",
    thisWorldPremise: "Here, a flawless resume is viewed with extreme suspicion as either a sign of cowardice or deceptive bookkeeping.",
    standingLabel: "Senior Assayer of Diagnostic Setbacks",
    socialClassLabel: "Grand Chancellor of the Crucible",
    wealthBracket: "Resiliently Endowed",
    wealthExplanation: "Maintained by the Public Safety Endowment, which rewards practitioners who openly publish forensic post-mortems of their own errors.",
    workplaceType: "A spacious laboratory and lecture hall filled with dissected broken apparatuses, cracked masonry samples, and transparent case-study scrolls.",
    societySummary: "A courageous, psychologically safe society where experimentation is bold because learning from failure is universally celebrated.",
    lawsSummary: "The Transparency Immunity Act guarantees total legal protection for any professional who immediately and publicly reports an operational blunder.",
    cultureSummary: "Annual Banquets of the Broken Gear where leaders toast their worst mistakes and the profound wisdom gained from surviving them.",
    institutionsSummary: "The Forensic Academy of Catastrophic Insight, the Broken Pillar Memorial, and the Guild of Honest Confession.",
    expectationsSummary: "When an experiment collapses, your duty is to brew tea for your apprentices and spend three days drawing diagrams of why it fell.",
    opportunitySummary: "High advisory appointments on emergency councils for those who have navigated deep crisis without hiding the scars.",
    divergenceD1: {
      id: "d1",
      title: "The End of Blame Culture",
      text: "Punitive liability was replaced by open-source root cause investigations after catastrophic coverups nearly caused civil collapse.",
      effects: "Defects are identified and neutralized years before they can cause fatal public accidents.",
    },
    divergenceD2: {
      id: "d2",
      title: "The Audited Blunder Resume",
      text: "Job applications require candidates to list three significant errors and explain the exact systemic adjustments they instituted afterward.",
      effects: "Arrogant, defensive personalities are filtered out of leadership before they can do harm.",
    },
    divergenceD3: {
      id: "d3",
      title: "Psychological Fearlessness",
      text: "Children grow up without the crippling fear of falling short, resulting in extraordinary creative vitality.",
      effects: "Art, architecture, and engineering produce bold, experimental leaps unconstrained by timid conformity.",
    },
    divergenceD4: {
      id: "d4",
      title: "The Memorial of the Crack",
      text: "Public monuments incorporate fragments of past infrastructure failures to remind citizens of the fragility of human works.",
      effects: "Civic pride is tempered by profound humility and ongoing scientific vigilance.",
    },
  },
  {
    id: "economy_of_recollection",
    name: "The Archive of Borrowed Days",
    invertedRule: "True status is measured by how much sensory memory of childhood, nature, and shared kindness you have protected from transaction.",
    earthPremise: "Earth measures net worth by numbers in digital bank accounts, ignoring the emotional amnesia caused by chronic overwork.",
    thisWorldPremise: "Here, those who remember how the river tasted thirty summers ago are the true aristocrats of living consciousness.",
    standingLabel: "Curator of the Sacred Recollection",
    socialClassLabel: "Patrician of Intact Wonder",
    wealthBracket: "Sensory Abundance",
    wealthExplanation: "Treasured across the district for keeping uncorrupted mental chronicles of municipal history and forgotten folkways.",
    workplaceType: "An apothecary of scent, tactile stones, and illuminated manuscript ledgers designed to awaken dormant human memories.",
    societySummary: "A contemplative culture that values authentic human consciousness over artificial financial accumulations.",
    lawsSummary: "The Integrity of Memory Treaty bans any coercive economic extraction of personal recollections or private childhood journals.",
    cultureSummary: "Evenings are spent around communal hearths recounting the sensory crispness of past seasons rather than discussing money.",
    institutionsSummary: "The Central Repository of Intact Days, the Order of Sensory Witness, and the Hearth of Common Song.",
    expectationsSummary: "Never sacrifice a vivid memory of friendship or natural wonder for a fleeting commercial advancement.",
    opportunitySummary: "Venerated roles as communal elders, counselors, and living historical chronicles for the next generation.",
    divergenceD1: {
      id: "d1",
      title: "The Currency of Direct Experience",
      text: "Civilization recognized that attention and authentic memory are the only irreplaceable human wealth.",
      effects: "Advertising, cognitive hijacking, and algorithmic attention-stripping were strictly banned.",
    },
    divergenceD2: {
      id: "d2",
      title: "Generational Oral Bridges",
      text: "Municipal history is preserved by trained human rememberers rather than dry, sterile administrative databases.",
      effects: "Laws and cultural codes retain deep emotional texture, empathy, and ancestral context.",
    },
    divergenceD3: {
      id: "d3",
      title: "Protection of Childhood Sanctuaries",
      text: "Early years are completely sheltered from competitive academic testing or economic commercialization.",
      effects: "Adults enter life with rich, joyful emotional reservoirs that insulate them from cynicism.",
    },
    divergenceD4: {
      id: "d4",
      title: "Tactile Heritage Preservation",
      text: "Public architecture incorporates textured stone, native woods, and water courses tuned to evoke calming nostalgic resonance.",
      effects: "Urban centers cultivate psychological grounding and emotional stability across generations.",
    },
  },
  {
    id: "doctrine_of_the_flaw",
    name: "The Guild of Intentional Imperfection",
    invertedRule: "Geometric perfection is regarded as arrogant hubris; every masterpiece, treaty, and civic structure must contain a deliberate flaw that lets the soul breathe.",
    earthPremise: "Earth worships algorithmic precision, slick symmetry, and sterile mass-manufactured uniformity.",
    thisWorldPremise: "Here, true mastery is demonstrated by knowing exactly where and how to introduce the sacrificial defect that invites human warmth.",
    standingLabel: "Master of the Sacred Aberration",
    socialClassLabel: "High Artisan of the Living Asymmetry",
    wealthBracket: "Mastercraft Equity",
    wealthExplanation: "Revered by municipal councils and guild halls who commission bespoke work specifically for its humane, deliberate individuality.",
    workplaceType: "A lively atelier filled with hand chisels, eccentric measuring calipers, and pottery wheels that weave charming deviations into clay.",
    societySummary: "A warm, delightfully idiosyncratic civilization where human vulnerability is woven visibly into every street and institution.",
    lawsSummary: "The Ordinance of the Living Stitch mandates that no legal document or public monument may be executed without an intentional human quirk.",
    cultureSummary: "Celebrates the wabi-sabi grace of worn wood, asymmetrical bridges, and the humorous fallibility of daily mortal life.",
    institutionsSummary: "The Bench of the Chipped Goblet, the Fellowship of Organic Asymmetry, and the Guild of Humane Craft.",
    expectationsSummary: "When your work looks dangerously close to sterile perfection, step back, smile, and introduce the flaw that gives it a heartbeat.",
    opportunitySummary: "Immense demand for artisans, architects, and diplomats who can rescue cold bureaucratic systems with warm human imperfection.",
    divergenceD1: {
      id: "d1",
      title: "Rejection of Sterile Precision",
      text: "Mass factory uniformity was rejected in favor of humanized manufacturing that honors material idiosyncrasies.",
      effects: "Every physical object carries unique personality, tactile warmth, and repairable character.",
    },
    divergenceD2: {
      id: "d2",
      title: "The Humility Mandate in Law",
      text: "Legal statutes include explicit acknowledgment of their own limits and invite judicial compassion in borderline cases.",
      effects: "Courts prioritize human justice and reconciliation over cold, merciless letter-of-the-law technicalities.",
    },
    divergenceD3: {
      id: "d3",
      title: "Humor as Structural Anchor",
      text: "Public ceremonies feature ceremonial fools who gently mock the solemnity of magistrates and master builders.",
      effects: "Political tyranny cannot flourish where pomposity is constantly punctured by affectionate civic humor.",
    },
    divergenceD4: {
      id: "d4",
      title: "Psychological Relief from Perfectionism",
      text: "Growing up in a world of celebrated flaws frees citizens from chronic performance anxiety and impostor syndrome.",
      effects: "Mental health and social camaraderie thrive across all ages and social tiers.",
    },
  },
  {
    id: "the_canon_of_restraint",
    name: "The Commonwealth of Honorable Refusal",
    invertedRule: "Monuments and medals are awarded solely for things a person chose NOT to do; heroic abstention outranks aggressive physical intervention.",
    earthPremise: "Earth rewards relentless physical expansion, aggressive construction, and visible conquest.",
    thisWorldPremise: "Here, the highest decorations belong to leaders who declined to build over the wetland, declined to wage the war, and left nature undisturbed.",
    standingLabel: "Knight Commander of the Untouched Horizon",
    socialClassLabel: "Steward of Divine Abstention",
    wealthBracket: "Peacefully Sustained",
    wealthExplanation: "Honored with hereditary civic stipends granted for preserving agricultural valleys and historical quarters from aggressive development.",
    workplaceType: "A quiet glass observatory overlooking vast untouched marshes, forested hills, and unchannelized wild rivers.",
    societySummary: "An ecologically serene commonwealth where open space, unwritten books, and unmined mountains are celebrated as supreme achievements.",
    lawsSummary: "The Abstention Laurel grants higher tax immunity to citizens who withdraw commercial development claims than to those who develop them.",
    cultureSummary: "Public squares feature empty marble plinths dedicated to generals who resigned their commissions and engineers who said 'no'.",
    institutionsSummary: "The High Tribunal of Restraint, the Registry of Preserved Voids, and the Order of the Unbroken Seal.",
    expectationsSummary: "Before executing any new project, you must present five compelling reasons why leaving the ground untouched would not be superior.",
    opportunitySummary: "Revered authority for ecological arbitrators, conservationists, and philosophical mediators who protect silence and space.",
    divergenceD1: {
      id: "d1",
      title: "The Inversion of Monumentality",
      text: "Civilization recognized that unbuilt landscapes and unconsumed resources are infinitely more precious than concrete monuments.",
      effects: "Forests, river basins, and open air remain pristine, sustaining immense ecological abundance.",
    },
    divergenceD2: {
      id: "d2",
      title: "Honors for the Road Not Taken",
      text: "Resumes highlight the lucrative, destructive opportunities a candidate deliberately declined out of ethical modesty.",
      effects: "Ethical integrity is verified by documented sacrifices rather than empty mission statements.",
    },
    divergenceD3: {
      id: "d3",
      title: "De-escalation of Conflict",
      text: "International disputes are defused because leaders gain higher domestic esteem by de-escalating than by rattling sabers.",
      effects: "Centuries of stable diplomatic peace without militarized standing armies.",
    },
    divergenceD4: {
      id: "d4",
      title: "The Elegance of the Unsaid",
      text: "Literature and discourse prize subtle, evocative restraint over sensationalist noise and polemical screaming.",
      effects: "Public dialogue is courteous, deep, and grounded in mutual respect.",
    },
  },
  {
    id: "the_fluidity_of_chronology",
    name: "The Terraced Tempo Commonwealth",
    invertedRule: "Time is experienced and governed as an elastic, local terrain rather than a rigid synchronized factory clock; schedules yield to conversational and organic ripeness.",
    earthPremise: "Earth forces everyone to submit to identical, merciless seconds, causing chronic rush, alarm clocks, and synchronized commuter panic.",
    thisWorldPremise: "Here, time is allowed to breathe; assemblies meet when thoughts are clear, and meetings conclude when peace is reached.",
    standingLabel: "Master of Harmonious Ripening",
    socialClassLabel: "Tempo Harmonizer",
    wealthBracket: "Temporally Rich",
    wealthExplanation: "Compensated in leisure credits and unpressured sabbaticals, insulated from any artificial deadline penalties.",
    workplaceType: "A terraced stone pavilion equipped with sand balances, water clepsydras, and celestial sundials tuned to natural light cycles.",
    societySummary: "A humane, unhurried civilization where mental well-being and genuine craftsmanship are never compromised for a calendar date.",
    lawsSummary: "The Ripeness Statute renders any contract null and void if either signatory can prove they were forced to sign under artificial time pressure.",
    cultureSummary: "Greetings inquire after the depth and texture of your morning rather than whether you are running on time.",
    institutionsSummary: "The Guild of Natural Rhythms, the Clepsydra Conclave, and the Assembly of Unhurried Deliberation.",
    expectationsSummary: "Never terminate an honest, heartfelt conversation simply because a mechanical clock struck an arbitrary hour.",
    opportunitySummary: "Deep public affection for mediators, healers, and craftsmen who take the necessary time to do work of unshakeable quality.",
    divergenceD1: {
      id: "d1",
      title: "The Abolition of the Factory Clock",
      text: "Mechanical synchronization was decoupled from civic obligations, returning time to natural solar, biological, and seasonal cycles.",
      effects: "Stress-related cardiovascular ailments and sleep deprivation syndromes are virtually non-existent.",
    },
    divergenceD2: {
      id: "d2",
      title: "The Principle of Organic Completion",
      text: "Projects are evaluated by their structural excellence upon completion rather than how fast they were assembled.",
      effects: "Infrastructure, literature, and craft endure for generations without premature decay.",
    },
    divergenceD3: {
      id: "d3",
      title: "Terraced Daily Pacing",
      text: "Cities stagger activity naturally: contemplation in the morning mist, creative work at midday, celebration at dusk.",
      effects: "Traffic jams, rush hours, and crowded bottlenecks are naturally eliminated.",
    },
    divergenceD4: {
      id: "d4",
      title: "Sovereignty Over Attention",
      text: "Citizens own their schedule as an inviolable personal right; no institution can demand instantaneous notification response.",
      effects: "Deep focus, sustained reading, and profound intellectual contemplation become ordinary daily reality.",
    },
  },
];
